/**
 * @file types.ts
 * @description Shared type definitions for the API Visual Debugger module.
 * These are the public-facing types that would be exported in an NPM package.
 */

// ─── Request State Types ──────────────────────────────────────────────────────

export type ApiRequestStatus = 'pending' | 'success' | 'error';
export type ApiEnvironment = 'SSR' | 'Client';

export interface StackFrame {
  fn: string;
  file: string;
  line: string;
  column: string;
}

export interface ApiRequest {
  /** Unique identifier for this request, used to correlate onRequest → onResponse. */
  id: string;
  url: string;
  method: string;
  status: ApiRequestStatus;
  /** HTTP status code received from the server, null while pending. */
  statusCode: number | null;
  /** Unix timestamp (ms) when the request was initiated. */
  startedAt: number;
  /** Round-trip duration in milliseconds, null while pending. */
  duration: number | null;
  environment: ApiEnvironment;
  /** Raw Error.stack string captured at the call site. */
  stackTrace: string;
  /**
   * Parsed and filtered StackFrame objects derived from stackTrace.
   * Populated lazily on the client (parsed by the overlay on demand) —
   * may be `undefined` for entries just added by the interceptor.
   */
  stackFrames?: StackFrame[];
  /**
   * The extracted trigger function name from the call stack.
   * Populated lazily on the client — may be `undefined` for entries just
   * added by the interceptor.
   */
  trigger?: string;
}

// ─── Configuration Type ───────────────────────────────────────────────────────

export interface DebuggerConfig {
  /**
   * Explicitly enable or disable the debugger. When omitted, the resolved
   * default falls back to `process.env.NODE_ENV === 'development'`.
   */
  enabled?: boolean;

  /**
   * Position of the floating badge and panel on screen.
   * @default 'bottom-right'
   */
  position: 'bottom-left' | 'bottom-right';

  /**
   * Optional list of URL filters. Each entry can be:
   * - A **string**: treated as a substring match against the full request URL.
   * - A **RegExp**: tested against the full request URL.
   *
   * If the array is empty or undefined, **all** requests are logged.
   *
   * @example ['vrid', 'checkout', /^\/v1\/products/]
   */
  urlFilters: Array<string | RegExp>;

  /**
   * Maximum number of requests to retain in the store.
   * Oldest requests are discarded when the cap is reached.
   * @default 100
   */
  maxRequests: number;

  /**
   * When `true`, emits a collapsed console group with a native async stack
   * trace for every intercepted request. Leverages Chrome's async stack
   * stitching so you can trace the call back to its originating component.
   * @default false
   */
  logToConsole: boolean;

  /**
   * When `true`, clears all recorded requests (both Client-side Zustand state
   * and the SSR server-side cache) on initial client mount — i.e. on every
   * full page reload the debugger starts with a clean slate.
   *
   * When `false`, the client `requests` array is persisted to `sessionStorage`
   * so it survives soft navigations and reloads within the same tab session.
   * (SSR requests are not persisted; they always come from the live server
   * cache polled every second.)
   *
   * @default true
   */
  clearOnReload: boolean;
}

/** Resolved defaults for DebuggerConfig — all fields required. */
export const DEFAULT_CONFIG: DebuggerConfig = {
  enabled: process.env.NODE_ENV === 'development',
  position: 'bottom-right',
  urlFilters: [],
  maxRequests: 100,
  logToConsole: false,
  clearOnReload: true,
};
